import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { devicesAPI } from '../api/devices';
import { deviceTypesAPI } from '../api/deviceTypes';
import { locationsAPI } from '../api/locations';
import { characteristicsAPI } from '../api/characteristics';
import { dataTypesAPI } from '../api/dataTypes';

function IncompleteDevices() {
  const [devices, setDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Edit dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    device_type_id: '',
    location_id: null,
    characteristic_values: {}
  });
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  // Load device types for filter
  useEffect(() => {
    const loadDeviceTypes = async () => {
      try {
        const data = await deviceTypesAPI.getAll();
        setDeviceTypes(data);
      } catch (err) {
        console.error('Failed to load device types:', err);
      }
    };

    loadDeviceTypes();
  }, []);

  // Load characteristics and locations
  useEffect(() => {
    const loadData = async () => {
      try {
        const [chars, locs, dTypes] = await Promise.all([
          characteristicsAPI.getAll(),
          locationsAPI.getAll(),
          dataTypesAPI.getAll()
        ]);

        setCharacteristics(chars);
        setLocations(locs);
        setDataTypes(dTypes);
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };

    loadData();
  }, []);

  // Load incomplete devices
  const loadIncompleteDevices = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        skip: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString()
      });

      if (selectedDeviceType) {
        params.append('device_type_id', selectedDeviceType);
      }

      const response = await fetch(`/api/devices/incomplete?${params}`);

      if (!response.ok) {
        throw new Error('Failed to load incomplete devices');
      }

      const data = await response.json();
      setDevices(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading incomplete devices:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncompleteDevices();
  }, [page, rowsPerPage, selectedDeviceType]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeviceTypeChange = (event) => {
    setSelectedDeviceType(event.target.value);
    setPage(0);
  };

  const handleOpenEditDialog = async (deviceId) => {
    try {
      // Load device details
      const device = await devicesAPI.getById(deviceId);
      setEditingDevice(device);

      // Convert characteristic_values from array to object
      const charValues = {};
      if (device.characteristic_values && Array.isArray(device.characteristic_values)) {
        device.characteristic_values.forEach(cv => {
          charValues[cv.characteristic_id] = cv.value;
        });
      }

      setFormData({
        name: device.name,
        device_type_id: device.device_type_id,
        location_id: device.location_id,
        characteristic_values: charValues
      });

      setOpenDialog(true);
      setSaveError('');
    } catch (err) {
      setError('Ошибка загрузки устройства: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDevice(null);
    setFormData({
      name: '',
      device_type_id: '',
      location_id: null,
      characteristic_values: {}
    });
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCharacteristicChange = (characteristicId, value) => {
    setFormData({
      ...formData,
      characteristic_values: {
        ...formData.characteristic_values,
        [characteristicId]: value
      }
    });
  };

  const handleSave = async () => {
    if (!editingDevice) return;

    setSaving(true);
    setSaveError('');

    try {
      const payload = {
        name: formData.name,
        device_type_id: formData.device_type_id,
        location_id: formData.location_id || null,
        characteristic_values: formData.characteristic_values
      };

      await devicesAPI.update(editingDevice.id, payload);
      await loadIncompleteDevices();
      handleCloseDialog();
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (deviceId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(deviceId)) {
      newExpanded.delete(deviceId);
    } else {
      newExpanded.add(deviceId);
    }
    setExpandedRows(newExpanded);
  };

  // Get characteristics for selected device type
  const getDeviceTypeCharacteristics = () => {
    if (!formData.device_type_id) return [];

    const deviceType = deviceTypes.find(dt => dt.id === formData.device_type_id);
    if (!deviceType || !deviceType.characteristics) return [];

    return deviceType.characteristics
      .sort((a, b) => a.display_order - b.display_order);
  };

  // Group devices by device type for statistics
  const deviceTypeGroups = devices.reduce((acc, device) => {
    const typeName = device.device_type_name;
    if (!acc[typeName]) {
      acc[typeName] = {
        count: 0,
        devices: []
      };
    }
    acc[typeName].count++;
    acc[typeName].devices.push(device);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Устройства с незаполненными полями
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Список устройств, у которых не заполнены обязательные характеристики
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      {!loading && devices.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Группировка по типам устройств
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {Object.entries(deviceTypeGroups).map(([typeName, group]) => (
              <Chip
                key={typeName}
                icon={<WarningIcon />}
                label={`${typeName}: ${group.count}`}
                color="warning"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          select
          label="Фильтр по типу устройства"
          value={selectedDeviceType}
          onChange={handleDeviceTypeChange}
          fullWidth
          size="small"
        >
          <MenuItem value="">Все типы</MenuItem>
          {deviceTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="40px"></TableCell>
              <TableCell>Название устройства</TableCell>
              <TableCell>Тип устройства</TableCell>
              <TableCell>Локация</TableCell>
              <TableCell align="center">Незаполненных полей</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Все устройства заполнены корректно
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <React.Fragment key={device.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(device.id)}
                      >
                        {expandedRows.has(device.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{device.name}</TableCell>
                    <TableCell>{device.device_type_name}</TableCell>
                    <TableCell>{device.location_name || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={device.missing_count}
                        color="warning"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditDialog(device.id)}
                      >
                        Заполнить
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedRows.has(device.id) ? 1 : 0 }}>
                      <Collapse in={expandedRows.has(device.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, backgroundColor: 'action.hover' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Незаполненные обязательные поля:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {device.missing_characteristics.map((char) => (
                              <Chip
                                key={char.id}
                                label={char.name}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} из ${count !== -1 ? count : `более ${to}`}`
          }
        />
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Редактировать устройство
        </DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Название"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            helperText="Уникальное название устройства"
          />

          <TextField
            fullWidth
            select
            label="Тип устройства"
            name="device_type_id"
            value={formData.device_type_id}
            onChange={handleChange}
            margin="normal"
            disabled
          >
            {deviceTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Локация"
            name="location_id"
            value={formData.location_id || ''}
            onChange={handleChange}
            margin="normal"
          >
            <MenuItem value="">Без локации</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Характеристики
          </Typography>

          <Grid container spacing={2}>
            {getDeviceTypeCharacteristics().map((dtc) => {
              const char = characteristics.find(c => c.id === dtc.characteristic_id);
              if (!char) return null;

              const dataType = dataTypes.find(dt => dt.id === char.data_type_id);
              const value = formData.characteristic_values[dtc.characteristic_id] || '';

              let inputComponent;

              switch (dataType?.base_type) {
                case 'enum':
                  inputComponent = (
                    <TextField
                      fullWidth
                      select
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                    >
                      {dataType.enum_values && dataType.enum_values.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                  break;

                case 'boolean':
                  inputComponent = (
                    <TextField
                      fullWidth
                      select
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                    >
                      <MenuItem value="true">Да</MenuItem>
                      <MenuItem value="false">Нет</MenuItem>
                    </TextField>
                  );
                  break;

                case 'date':
                  inputComponent = (
                    <TextField
                      fullWidth
                      type="date"
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                  break;

                case 'datetime':
                  inputComponent = (
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                  break;

                case 'integer':
                case 'port':
                  inputComponent = (
                    <TextField
                      fullWidth
                      type="number"
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                      placeholder={dataType.placeholder}
                    />
                  );
                  break;

                case 'float':
                  inputComponent = (
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ step: 0.01 }}
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                      placeholder={dataType.placeholder}
                    />
                  );
                  break;

                case 'text':
                  inputComponent = (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description}
                      placeholder={dataType.placeholder}
                    />
                  );
                  break;

                default:
                  inputComponent = (
                    <TextField
                      fullWidth
                      label={char.name + (dtc.is_required ? ' *' : '')}
                      value={value}
                      onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
                      margin="normal"
                      helperText={char.description || dataType?.help_text}
                      placeholder={dataType?.placeholder}
                    />
                  );
              }

              return (
                <Grid item xs={12} md={6} key={dtc.characteristic_id}>
                  {inputComponent}
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default IncompleteDevices;
